import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { SitesAutoComplete } from '@/components/common';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { storageLocationTypes } from '@/pages/semen-app/util/constants';
import {
  StorageLocationFormValues,
  storageLocationSchema,
} from '@/pages/semen-app/util/schema';
import { useAddStorageLocation } from '@/services/semen-service/hooks';

type AddLocationFormProps = {
  gridRef?: React.RefObject<AgGridReact>;
  fertilizationSiteId?: string;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const AddLocationForm = ({
  gridRef,
  fertilizationSiteId,
  onCancel,
  onSuccess,
}: AddLocationFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: addStorageLocation } = useAddStorageLocation();

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<StorageLocationFormValues>({
    resolver: zodResolver(storageLocationSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      tank: '',
      row: '',
      canister: '',
      locationType: undefined as unknown as number,
      siteId: fertilizationSiteId || '',
    },
  });

  useEffect(() => {
    if (fertilizationSiteId) {
      setValue('siteId', fertilizationSiteId, { shouldValidate: true });
    }
  }, [fertilizationSiteId, setValue]);

  const handleSaveStorageLocation = async (data: StorageLocationFormValues) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const payload = {
        Name: data.name,
        Tank: data.tank || '',
        Row: data.row || '',
        Canister: data.canister || '',
        LocationType: data.locationType,
        SiteId: data.siteId,
      };

      const res = await addStorageLocation(payload);
      if (res?.success === false) {
        dispatch(
          addToast({
            severity: 'warn',
            summary: t('common.error'),
            detail: res.message,
            life: ToastLife.MEDIUM,
          }),
        );
        return;
      }

      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail:
            res.message ||
            t('storageLocation.storageLocationCreatedSuccessfully'),
          life: ToastLife.MEDIUM,
        }),
      );

      gridRef?.current?.api?.refreshInfiniteCache();
      reset();
      onSuccess?.();
      onCancel();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t('storageLocation.failedToCreateStorageLocation');
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: errorMessage,
          life: ToastLife.LONG,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="storage-location-form" className="flex flex-col gap-3">
      <div>
        <label className="mb-1 block text-sm font-medium">
          {t('fields.nameRequired')}
        </label>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <>
              <InputText {...field} className="w-full" />
              {errors.name && (
                <small className="p-error">{errors.name.message}</small>
              )}
            </>
          )}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">
          {t('storageLocation.tank')}
        </label>
        <Controller
          control={control}
          name="tank"
          render={({ field }) => <InputText {...field} className="w-full" />}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">
          {t('storageLocation.row')}
        </label>
        <Controller
          control={control}
          name="row"
          render={({ field }) => <InputText {...field} className="w-full" />}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">
          {t('storageLocation.canister')}
        </label>
        <Controller
          control={control}
          name="canister"
          render={({ field }) => <InputText {...field} className="w-full" />}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">
          {t('storageLocation.locationTypeRequired')}
        </label>
        <Controller
          control={control}
          name="locationType"
          render={({ field }) => (
            <>
              <Dropdown
                {...field}
                value={field.value ?? null}
                options={storageLocationTypes}
                onChange={(e) => field.onChange(e.value)}
                placeholder={t('storageLocation.selectLocationType')}
                className="w-full"
              />
              {errors.locationType && (
                <small className="p-error">
                  {errors.locationType.message as string}
                </small>
              )}
            </>
          )}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">
          {t('site.siteRequired')}
        </label>
        <Controller
          name="siteId"
          control={control}
          render={({ field }) => (
            <>
              <SitesAutoComplete
                defaultValueId={field.value || undefined}
                onSelect={(e) => field.onChange(e.value?.id || undefined)}
                className={errors.siteId ? 'p-invalid' : ''}
                appendTo={null}
              />
              {errors.siteId && (
                <small className="p-error">{errors.siteId.message}</small>
              )}
            </>
          )}
        />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button
          label={t('common.cancel')}
          className="p-button-text"
          onClick={onCancel}
          disabled={isSubmitting}
        />
        <Button
          label={t('common.save')}
          icon="pi pi-check"
          onClick={handleSubmit(handleSaveStorageLocation)}
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </form>
  );
};
